import React, { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { AuthUserGroup, isHistorianUserGroup } from "~/types/auth";
import { LoadingSpinner } from "./LoadingSpinner";

interface ProtectedRouteProps {
  allowedGroups: AuthUserGroup[];
  children: JSX.Element;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedGroups,
  children,
}) => {
  const { userGroup, isLoading, authUser } = useAuthSession();
  const location = useLocation();
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    if (authUser) {
      wasAuthenticated.current = true;
    }
  }, [authUser]);

  // Show spinner ONLY on initial load.
  if (isLoading && !wasAuthenticated.current) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  // If we were authenticated, but the user object has been temporarily cleared
  // during a token refresh, we want to allow the user to stay on the page.
  // API calls may fail, but this prevents losing their work.
  if (!authUser && wasAuthenticated.current) {
    return children;
  }

  // If there's an auth error, or no user and was never authenticated, redirect to login
  if (!authUser) {
    // Don't add returnUrl for root path since it redirects to role-based home anyway
    if (location.pathname === "/") {
      return <Navigate to="/login" replace />;
    }
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // If user exists but has no group or is unauthorized, still redirect to login
  if (!userGroup || userGroup === AuthUserGroup.UNAUTHORIZED) {
    // Don't add returnUrl for root path since it redirects to role-based home anyway
    if (location.pathname === "/") {
      return <Navigate to="/login" replace />;
    }
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // If user doesn't have permission for this route
  if (!allowedGroups.includes(userGroup)) {
    if (isHistorianUserGroup(userGroup)) {
      return <Navigate to="/in-person-evaluations" replace />;
    }
    return <Navigate to="/not-found" replace />;
  }

  return children;
};
